import React, { useRef, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    LinearProgress,
} from '@mui/material';
import { VolumeUp, Compare, Download, PlayArrow, Pause } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ReadingAttempt, WaveformData } from '@/services/reading/types';

interface WaveformDisplayProps {
    isRecording: boolean;
    attempt?: ReadingAttempt;
    showComparison?: boolean;
    height?: number;
    showControls?: boolean;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({
    isRecording,
    attempt,
    showComparison = false,
    height = 120,
    showControls = true,
}) => {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>();
    const [waveformData, setWaveformData] = useState<WaveformData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        if (attempt && attempt.id) {
            loadWaveformData();
        }
    }, [attempt?.id]);

    useEffect(() => {
        if (isRecording) {
            startLiveWaveform();
        } else {
            stopLiveWaveform();
        }

        return () => {
            stopLiveWaveform();
        };
    }, [isRecording]);

    useEffect(() => {
        if (attempt && !isRecording) {
            loadWaveformData();
        }
    }, [attempt, isRecording]);

    const loadWaveformData = async () => {
        if (!attempt?.id) return;

        setIsLoading(true);
        setError(null);

        try {
            const { default: readingService } = await import('@/services/reading/readingService');
            const data = await readingService.getWaveformData(attempt.id);
            setWaveformData(data);

            if (data.samples && data.samples.length > 0) {
                drawStaticWaveform(data.samples);
            }
        } catch (err) {
            setError('Failed to load waveform data');
            console.error('Waveform error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const startLiveWaveform = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const context = new AudioContext();
            const source = context.createMediaStreamSource(stream);
            const analyserNode = context.createAnalyser();

            analyserNode.fftSize = 256;
            source.connect(analyserNode);

            const bufferLength = analyserNode.frequencyBinCount;
            const dataArrayBuffer = new Uint8Array(bufferLength);

            setAudioContext(context);

            drawLiveWaveform(analyserNode, dataArrayBuffer);
        } catch (err) {
            console.error('Error accessing microphone:', err);
            setError('Unable to access microphone for waveform display');
        }
    };

    const stopLiveWaveform = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (audioContext) {
            audioContext.close();
            setAudioContext(null);
        }
    };

    const drawLiveWaveform = (analyserNode: AnalyserNode, dataArrayBuffer: Uint8Array) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasCtx = canvas.getContext('2d');
        if (!canvasCtx) return;

        const draw = () => {
            animationRef.current = requestAnimationFrame(draw);

            analyserNode.getByteFrequencyData(dataArrayBuffer);

            canvasCtx.fillStyle = '#1976d2';
            canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

            const barWidth = (canvas.width / dataArrayBuffer.length) * 2.5;
            let barHeight;
            let x = 0;

            for (let i = 0; i < dataArrayBuffer.length; i++) {
                barHeight = (dataArrayBuffer[i] / 255) * canvas.height;

                // Create gradient for bars
                const gradient = canvasCtx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
                gradient.addColorStop(0, '#4fc3f7');
                gradient.addColorStop(1, '#81c784');

                canvasCtx.fillStyle = gradient;
                canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

                x += barWidth + 1;
            }
        };

        draw();
    };

    const drawStaticWaveform = (samples: number[]) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasCtx = canvas.getContext('2d');
        if (!canvasCtx) return;

        const width = canvas.width;
        const height = canvas.height;

        // Clear canvas
        canvasCtx.fillStyle = '#f5f5f5';
        canvasCtx.fillRect(0, 0, width, height);

        // Draw waveform
        canvasCtx.strokeStyle = '#1976d2';
        canvasCtx.lineWidth = 2;
        canvasCtx.beginPath();

        const sliceWidth = width / samples.length;
        let x = 0;

        for (let i = 0; i < samples.length; i++) {
            const v = (samples[i] + 1) / 2; // Normalize from [-1, 1] to [0, 1]
            const y = height - v * height;

            if (i === 0) {
                canvasCtx.moveTo(x, y);
            } else {
                canvasCtx.lineTo(x, y);
            }

            x += sliceWidth;
        }

        canvasCtx.stroke();

        // Draw quality indicators if available
        if (attempt) {
            drawQualityIndicators(canvasCtx, width, height);
        }
    };

    const drawQualityIndicators = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
        if (!attempt) return;

        // Draw pronunciation quality zones
        const pronScore = attempt.pronunciation_score;
        const fluencyScore = attempt.fluency_score;

        // Green zone for good pronunciation
        if (pronScore >= 80) {
            ctx.fillStyle = 'rgba(76, 175, 80, 0.1)';
            ctx.fillRect(0, 0, width, height * 0.3);
        }

        // Yellow zone for moderate pronunciation
        if (pronScore >= 60 && pronScore < 80) {
            ctx.fillStyle = 'rgba(255, 193, 7, 0.1)';
            ctx.fillRect(0, height * 0.3, width, height * 0.4);
        }

        // Red zone for poor pronunciation
        if (pronScore < 60) {
            ctx.fillStyle = 'rgba(244, 67, 54, 0.1)';
            ctx.fillRect(0, height * 0.7, width, height * 0.3);
        }
    };

    const handleDownloadWaveform = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const link = document.createElement('a');
        link.download = `waveform-${attempt?.sentence_number || 'recording'}.png`;
        link.href = canvas.toDataURL();
        link.click();
    };

    const handleCompareAudio = () => {
        // This would open the AudioComparison component
        if (attempt) {
            console.log('Opening audio comparison for attempt:', attempt.id);
        }
    };

    return (
        <Paper sx={{ p: 2 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <VolumeUp />
                    {isRecording ? 'Recording...' : t('studentReading.practice.interface.pronunciationAnalysis')}
                    {isRecording && <Chip label="Recording" color="error" size="small" variant="outlined" />}
                </Typography>

                {showControls && (
                    <Box display="flex" gap={1}>
                        {attempt && (
                            <>
                                <Tooltip title="Compare with reference">
                                    <IconButton size="small" onClick={handleCompareAudio}>
                                        <Compare />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Download waveform">
                                    <IconButton size="small" onClick={handleDownloadWaveform}>
                                        <Download />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* Loading State */}
            {isLoading && (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight={height}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                        Loading waveform data...
                    </Typography>
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                    <Button size="small" onClick={loadWaveformData} sx={{ ml: 1 }}>
                        Retry
                    </Button>
                </Alert>
            )}

            {/* Canvas */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <canvas
                    ref={canvasRef}
                    width={800}
                    height={height}
                    style={{
                        width: '100%',
                        height: `${height}px`,
                        display: 'block',
                        backgroundColor: isRecording ? '#e3f2fd' : '#fafafa',
                    }}
                />
            </Box>

            {/* Waveform Info */}
            {waveformData && (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Audio Analysis:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            label={`Duration: ${waveformData.duration.toFixed(2)}s`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip label={`Sample Rate: ${waveformData.sample_rate}Hz`} size="small" variant="outlined" />
                        {waveformData.rms_energy !== undefined && (
                            <Chip
                                label={`Energy: ${(waveformData.rms_energy * 100).toFixed(1)}%`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {waveformData.zero_crossing_rate !== undefined && (
                            <Chip
                                label={`ZCR: ${(waveformData.zero_crossing_rate * 100).toFixed(1)}%`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* Recording Quality Indicators */}
            {attempt && (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Recording Quality:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            label={`Pronunciation: ${Math.round(attempt.pronunciation_score)}%`}
                            color={
                                attempt.pronunciation_score >= 80
                                    ? 'success'
                                    : attempt.pronunciation_score >= 60
                                    ? 'warning'
                                    : 'error'
                            }
                            size="small"
                        />
                        <Chip
                            label={`Fluency: ${Math.round(attempt.fluency_score)}%`}
                            color={
                                attempt.fluency_score >= 80
                                    ? 'success'
                                    : attempt.fluency_score >= 60
                                    ? 'warning'
                                    : 'error'
                            }
                            size="small"
                        />
                        {attempt.intonation_score !== undefined && (
                            <Chip
                                label={`Intonation: ${Math.round(attempt.intonation_score)}%`}
                                color={
                                    attempt.intonation_score >= 80
                                        ? 'success'
                                        : attempt.intonation_score >= 60
                                        ? 'warning'
                                        : 'error'
                                }
                                size="small"
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* Comparison Mode */}
            {showComparison && attempt && (
                <Box mt={2}>
                    <Alert severity="info">
                        Comparison mode active. The waveform shows your recording quality analysis.
                    </Alert>
                </Box>
            )}

            {/* Audio Player Controls */}
            {attempt?.audio_url && showControls && !isRecording && (
                <Box mt={2} display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => setIsPlaying(!isPlaying)} color="primary">
                        {isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>

                    <Box flexGrow={1}>
                        <LinearProgress
                            variant="determinate"
                            value={0} // This would be connected to actual audio progress
                            sx={{ height: 6, borderRadius: 3 }}
                        />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        0:00 / {attempt.audio_duration || 0}s
                    </Typography>

                    <IconButton size="small">
                        <VolumeUp />
                    </IconButton>
                </Box>
            )}
        </Paper>
    );
};

export default WaveformDisplay;
